import { AxiosError } from 'axios';
import { useCallback, useEffect, useRef } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import {
  MapAxiosErrorToRocketError,
  RocketError,
  buildRocketErrorMessage,
  getAxiosErrorResponseMessage,
} from '../api/errors';
import { useSnackbar } from '../hooks/useSnackbar';
import { useTranslation } from '../i18n';

export interface ErrorHandlerConfig {
  mapAxiosErrorToRocketError: MapAxiosErrorToRocketError;
  getRedirectPath: (errorKey: string) => string | null;
  dataFetchingErrorKey: string;
  renderErrorRedirect: string | null;
  resetKey?: string;
  navigate: (path: string) => void;
}

interface Props extends ErrorHandlerConfig {
  readonly children: React.ReactNode;
}

function ErrorFallback({
  error,
  resetErrorBoundary,
  handleBoundaryError,
}: {
  error: unknown;
  resetErrorBoundary: () => void;
  handleBoundaryError: (error: unknown) => boolean;
}) {
  const hasResetRef = useRef(false);

  useEffect(() => {
    if (hasResetRef.current) return;
    hasResetRef.current = true;
    const normalizedError = error instanceof Error ? error : new Error(String(error));
    const wasHandled = handleBoundaryError(normalizedError);
    if (wasHandled) {
      resetErrorBoundary();
    }
  }, [error, resetErrorBoundary, handleBoundaryError]);

  return null;
}

function ErrorHandlerContent({
  children,
  onRenderSuccess,
}: {
  readonly children: React.ReactNode;
  readonly onRenderSuccess: () => void;
}) {
  useEffect(() => {
    onRenderSuccess();
  }, [onRenderSuccess]);

  return children;
}

export function ErrorHandler({
  children,
  mapAxiosErrorToRocketError,
  getRedirectPath,
  dataFetchingErrorKey,
  renderErrorRedirect,
  resetKey,
  navigate,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const hasRedirectedRef = useRef(false);

  const handleRedirection = useCallback(
    (path: string) => {
      if (!hasRedirectedRef.current) {
        hasRedirectedRef.current = true;
        navigate(path);
        return true;
      }
      return false;
    },
    [navigate]
  );

  const handleRenderSuccess = useCallback(() => {
    hasRedirectedRef.current = false;
  }, []);

  const processRocketError = useCallback(
    (rocketError: RocketError) => {
      const errorKey = rocketError.message;
      const redirectPath = getRedirectPath(errorKey);

      if (redirectPath) {
        return handleRedirection(redirectPath);
      }

      if (errorKey === dataFetchingErrorKey) {
        enqueueSnackbar(
          buildRocketErrorMessage(rocketError.details, t('error_handler.could_not_fetch_data')),
          { key: dataFetchingErrorKey, variant: 'error' }
        );
        return true;
      }

      return false;
    },
    [handleRedirection, enqueueSnackbar, getRedirectPath, dataFetchingErrorKey, t]
  );

  // Handle unhandled promise rejections - async functions, API calls
  useEffect(() => {
    const handleUnhandledRejection = async (event: PromiseRejectionEvent) => {
      if (event.reason instanceof RocketError) {
        // Handle specific RocketErrors
        processRocketError(event.reason);
      } else if (event.reason instanceof AxiosError) {
        // Handle Axios errors - error from the server, or network errors
        const serverMessage = getAxiosErrorResponseMessage(event.reason);
        enqueueSnackbar(serverMessage || t('error_handler.could_not_perform_action'), {
          variant: 'error',
        });
      } else if (event.reason instanceof Error && event.reason.name === 'NotAllowedError') {
        // Handle browser permission errors
        enqueueSnackbar(t('error_handler.browser_permission_error'), { variant: 'error' });
      } else {
        // Do nothing for general errors
      }
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [enqueueSnackbar, processRocketError, t]);

  // Handle errors that occur in the ErrorBoundary - rendering errors, suspense query errors
  const handleBoundaryError = useCallback(
    (error: unknown) => {
      if (error instanceof RocketError) {
        return processRocketError(error);
      }

      if (error instanceof AxiosError) {
        // Error from a suspense query - handle it as an API error
        const rocketError = mapAxiosErrorToRocketError(error, 'query');
        if (rocketError) {
          return processRocketError(rocketError);
        }
      } else if (renderErrorRedirect) {
        // Rendering error - redirect to the configured fallback route
        return handleRedirection(renderErrorRedirect);
      }

      return false;
    },
    [handleRedirection, processRocketError, mapAxiosErrorToRocketError, renderErrorRedirect]
  );

  return (
    <ErrorBoundary
      resetKeys={resetKey ? [resetKey] : undefined}
      fallbackRender={({ error, resetErrorBoundary }) => (
        <ErrorFallback
          error={error}
          resetErrorBoundary={resetErrorBoundary}
          handleBoundaryError={handleBoundaryError}
        />
      )}
    >
      <ErrorHandlerContent onRenderSuccess={handleRenderSuccess}>{children}</ErrorHandlerContent>
    </ErrorBoundary>
  );
}
